import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { PhoneNumberField } from '@/components/common';
import {
  PUSH_DEVICE_TYPE_CHOICES,
  PUSH_PLATFORM_CHOICES_MOBILE,
  PUSH_PLATFORM_CHOICES_WEB,
} from '../../constants';
import type {
  GroupMember,
  GroupMemberNotificationChannels,
  PushDeviceType,
  PushPlatform,
} from '../../types/account';

interface Props {
  open: boolean;
  initial?: GroupMember;
  onCancel: () => void;
  onSubmit: (member: GroupMember) => void;
}

const emptyChannels = (): GroupMemberNotificationChannels => ({
  push: { deviceType: '', deviceTokens: [] },
  email: { addresses: [] },
  sms: { numbers: [] },
  telegram: { chatIds: [] },
  whatsApp: { numbers: [] },
});

const channelPanelSx = {
  border: '1px solid',
  borderColor: 'primary.light',
  borderRadius: 1,
  p: { xs: 1.5, sm: 2 },
  mt: 2,
};

interface RemoveButtonProps {
  label: string;
  onClick: () => void;
}

const RemoveButton: React.FC<RemoveButtonProps> = ({ label, onClick }) => (
  <Button
    variant="contained"
    color="error"
    onClick={onClick}
    aria-label={label}
    sx={{
      textTransform: 'none',
      flexShrink: 0,
      minWidth: { xs: 40, sm: 'auto' },
      px: { xs: 0, sm: 2 },
    }}
  >
    <CloseIcon
      fontSize="small"
      sx={{ display: { xs: 'inline-block', sm: 'none' } }}
    />
    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
      {label}
    </Box>
  </Button>
);

export const GroupMemberDialog: React.FC<Props> = ({
  open,
  initial,
  onCancel,
  onSubmit,
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [userId, setUserId] = useState('');
  const [userIdTouched, setUserIdTouched] = useState(false);
  const [channels, setChannels] = useState<GroupMemberNotificationChannels>(
    emptyChannels()
  );
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!open) return;
    setUserId(initial?.userId ?? '');
    // Each channel opens with one ready-to-type row — "Add …" is only for
    // extra entries. Blank rows are stripped again on submit.
    const seed = (arr: string[] | undefined): string[] =>
      arr && arr.length > 0 ? arr : [''];
    const seedTokens = (
      arr: GroupMemberNotificationChannels['push']['deviceTokens'] | undefined
    ) => (arr && arr.length > 0 ? arr : [{ token: '', platform: '' as const }]);
    setChannels({
      push: {
        deviceType: initial?.notificationChannels?.push?.deviceType ?? '',
        deviceTokens: seedTokens(
          initial?.notificationChannels?.push?.deviceTokens
        ),
      },
      email: {
        addresses: seed(initial?.notificationChannels?.email?.addresses),
      },
      sms: { numbers: seed(initial?.notificationChannels?.sms?.numbers) },
      telegram: {
        chatIds: seed(initial?.notificationChannels?.telegram?.chatIds),
      },
      whatsApp: {
        numbers: seed(initial?.notificationChannels?.whatsApp?.numbers),
      },
    });
    setTab(0);
    setUserIdTouched(false);
  }, [open, initial]);

  const canSubmit = userId.trim().length > 0;

  const submit = () => {
    if (!canSubmit) {
      setUserIdTouched(true);
      return;
    }
    // Strip the seeded/blank rows — only real values are persisted.
    const clean = (arr: string[]): string[] =>
      arr.map((v) => v.trim()).filter(Boolean);
    onSubmit({
      userId,
      notificationChannels: {
        push: {
          ...channels.push,
          deviceTokens: channels.push.deviceTokens.filter((t) =>
            t.token.trim()
          ),
        },
        email: { addresses: clean(channels.email.addresses) },
        sms: { numbers: clean(channels.sms.numbers) },
        telegram: { chatIds: clean(channels.telegram.chatIds) },
        whatsApp: { numbers: clean(channels.whatsApp.numbers) },
      },
    });
  };

  const updatePush = (
    patch: Partial<GroupMemberNotificationChannels['push']>
  ) =>
    setChannels((c) => ({
      ...c,
      push: { ...c.push, ...patch },
    }));

  const addPushToken = () => {
    updatePush({
      deviceTokens: [...channels.push.deviceTokens, { token: '', platform: '' }],
    });
  };
  const removePushToken = (index: number) => {
    updatePush({
      deviceTokens: channels.push.deviceTokens.filter((_, i) => i !== index),
    });
  };

  const addEntry = (key: 'email' | 'sms' | 'telegram' | 'whatsApp') => () => {
    setChannels((c) => {
      if (key === 'email') {
        return { ...c, email: { addresses: [...c.email.addresses, ''] } };
      }
      if (key === 'sms') {
        return { ...c, sms: { numbers: [...c.sms.numbers, ''] } };
      }
      if (key === 'telegram') {
        return {
          ...c,
          telegram: { chatIds: [...c.telegram.chatIds, ''] },
        };
      }
      return { ...c, whatsApp: { numbers: [...c.whatsApp.numbers, ''] } };
    });
  };

  const removeEntry =
    (key: 'email' | 'sms' | 'telegram' | 'whatsApp', index: number) => () => {
      setChannels((c) => {
        if (key === 'email') {
          return {
            ...c,
            email: {
              addresses: c.email.addresses.filter((_, i) => i !== index),
            },
          };
        }
        if (key === 'sms') {
          return {
            ...c,
            sms: { numbers: c.sms.numbers.filter((_, i) => i !== index) },
          };
        }
        if (key === 'telegram') {
          return {
            ...c,
            telegram: {
              chatIds: c.telegram.chatIds.filter((_, i) => i !== index),
            },
          };
        }
        return {
          ...c,
          whatsApp: {
            numbers: c.whatsApp.numbers.filter((_, i) => i !== index),
          },
        };
      });
    };

  const platformChoices =
    channels.push.deviceType === 'MOBILE'
      ? PUSH_PLATFORM_CHOICES_MOBILE
      : channels.push.deviceType === 'WEB'
      ? PUSH_PLATFORM_CHOICES_WEB
      : [];

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onCancel();
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        {translate('resources.accounts.groups.memberTitle')}
        <IconButton onClick={onCancel} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 3 } }}>
        <TextField
          label={translate('resources.accounts.fields.userId')}
          required
          fullWidth
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          onBlur={() => setUserIdTouched(true)}
          InputLabelProps={{ shrink: true }}
          error={userIdTouched && !canSubmit}
          helperText={
            userIdTouched && !canSubmit
              ? translate('resources.accounts.validation.required', {
                  field: translate('resources.accounts.fields.userId'),
                })
              : ' '
          }
          sx={{ mb: 2 }}
        />
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          {translate('resources.accounts.groups.channelsHeading')}
        </Typography>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={translate('resources.accounts.channels.push')} />
          <Tab label={translate('resources.accounts.channels.email')} />
          <Tab label={translate('resources.accounts.channels.sms')} />
          <Tab label={translate('resources.accounts.channels.telegram')} />
          <Tab label={translate('resources.accounts.channels.whatsapp')} />
        </Tabs>

        {/* Push */}
        {tab === 0 && (
          <Box sx={channelPanelSx}>
            <Typography sx={{ fontWeight: 600, mb: 2 }} color="primary">
              {translate('resources.accounts.channels.push')} Channel
            </Typography>
            {channels.push.deviceTokens.length > 0 && (
              <TextField
                select
                fullWidth
                sx={{ mb: 2 }}
                label={translate('resources.accounts.fields.deviceType')}
                InputLabelProps={{ shrink: true }}
                value={channels.push.deviceType}
                onChange={(e) =>
                  updatePush({
                    deviceType: e.target.value as PushDeviceType,
                  })
                }
              >
                {PUSH_DEVICE_TYPE_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {channels.push.deviceTokens.map((tok, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                  mb: { xs: 1.5, sm: 1 },
                  p: { xs: 1.5, sm: 0 },
                  border: { xs: '1px solid', sm: 'none' },
                  borderColor: { xs: 'grey.300', sm: 'transparent' },
                  borderRadius: { xs: 1, sm: 0 },
                  alignItems: { xs: 'stretch', sm: 'flex-start' },
                }}
              >
                <TextField
                  label={translate('resources.accounts.fields.token')}
                  value={tok.token}
                  onChange={(e) =>
                    updatePush({
                      deviceTokens: channels.push.deviceTokens.map((t, idx) =>
                        idx === i ? { ...t, token: e.target.value } : t
                      ),
                    })
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  sx={{
                    minWidth: { xs: 0, sm: '10rem' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                  label={translate('resources.accounts.fields.platform')}
                  InputLabelProps={{ shrink: true }}
                  value={tok.platform}
                  onChange={(e) =>
                    updatePush({
                      deviceTokens: channels.push.deviceTokens.map((t, idx) =>
                        idx === i
                          ? {
                              ...t,
                              platform: e.target.value as PushPlatform,
                            }
                          : t
                      ),
                    })
                  }
                >
                  {platformChoices.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Box
                  sx={{
                    order: { xs: -1, sm: 0 },
                    alignSelf: { xs: 'flex-end', sm: 'auto' },
                  }}
                >
                  <RemoveButton
                    label={translate('resources.accounts.actions.remove')}
                    onClick={() => removePushToken(i)}
                  />
                </Box>
              </Box>
            ))}
            <Button
              variant="contained"
              onClick={addPushToken}
              sx={{ textTransform: 'none', mt: 1 }}
            >
              {translate('resources.accounts.actions.addToken')}
            </Button>
          </Box>
        )}

        {/* Email */}
        {tab === 1 && (
          <Box sx={channelPanelSx}>
            <Typography sx={{ fontWeight: 600, mb: 2 }} color="primary">
              {translate('resources.accounts.channels.email')} Channel
            </Typography>
            {channels.email.addresses.map((addr, i) => (
              <Box
                key={i}
                sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-end' }}
              >
                <TextField
                  label={translate('resources.accounts.fields.emailAddress')}
                  value={addr}
                  onChange={(e) =>
                    setChannels((c) => ({
                      ...c,
                      email: {
                        addresses: c.email.addresses.map((a, idx) =>
                          idx === i ? e.target.value : a
                        ),
                      },
                    }))
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <RemoveButton
                  label={translate('resources.accounts.actions.remove')}
                  onClick={removeEntry('email', i)}
                />
              </Box>
            ))}
            <Button
              variant="contained"
              onClick={addEntry('email')}
              sx={{ textTransform: 'none', mt: 1 }}
            >
              {translate('resources.accounts.actions.addEmail')}
            </Button>
          </Box>
        )}

        {/* SMS */}
        {tab === 2 && (
          <Box sx={channelPanelSx}>
            <Typography sx={{ fontWeight: 600, mb: 2 }} color="primary">
              {translate('resources.accounts.channels.sms')} Channel
            </Typography>
            {channels.sms.numbers.map((n, i) => (
              <Box
                key={i}
                sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-end' }}
              >
                <PhoneNumberField
                  label={translate('resources.accounts.fields.number')}
                  value={n}
                  onChange={(next) =>
                    setChannels((c) => ({
                      ...c,
                      sms: {
                        numbers: c.sms.numbers.map((v, idx) =>
                          idx === i ? next : v
                        ),
                      },
                    }))
                  }
                />
                <RemoveButton
                  label={translate('resources.accounts.actions.remove')}
                  onClick={removeEntry('sms', i)}
                />
              </Box>
            ))}
            <Button
              variant="contained"
              onClick={addEntry('sms')}
              sx={{ textTransform: 'none', mt: 1 }}
            >
              {translate('resources.accounts.actions.addNumber')}
            </Button>
          </Box>
        )}

        {/* Telegram */}
        {tab === 3 && (
          <Box sx={channelPanelSx}>
            <Typography sx={{ fontWeight: 600, mb: 2 }} color="primary">
              {translate('resources.accounts.channels.telegram')} Channel
            </Typography>
            {channels.telegram.chatIds.map((id, i) => (
              <Box
                key={i}
                sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-end' }}
              >
                <TextField
                  label={translate('resources.accounts.fields.chatId')}
                  value={id}
                  onChange={(e) =>
                    setChannels((c) => ({
                      ...c,
                      telegram: {
                        chatIds: c.telegram.chatIds.map((v, idx) =>
                          idx === i ? e.target.value : v
                        ),
                      },
                    }))
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <RemoveButton
                  label={translate('resources.accounts.actions.remove')}
                  onClick={removeEntry('telegram', i)}
                />
              </Box>
            ))}
            <Button
              variant="contained"
              onClick={addEntry('telegram')}
              sx={{ textTransform: 'none', mt: 1 }}
            >
              {translate('resources.accounts.actions.addChatId')}
            </Button>
          </Box>
        )}

        {/* WhatsApp */}
        {tab === 4 && (
          <Box sx={channelPanelSx}>
            <Typography sx={{ fontWeight: 600, mb: 2 }} color="primary">
              {translate('resources.accounts.channels.whatsapp')} Channel
            </Typography>
            {channels.whatsApp.numbers.map((n, i) => (
              <Box
                key={i}
                sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-end' }}
              >
                <PhoneNumberField
                  label={translate('resources.accounts.fields.number')}
                  value={n}
                  onChange={(next) =>
                    setChannels((c) => ({
                      ...c,
                      whatsApp: {
                        numbers: c.whatsApp.numbers.map((v, idx) =>
                          idx === i ? next : v
                        ),
                      },
                    }))
                  }
                />
                <RemoveButton
                  label={translate('resources.accounts.actions.remove')}
                  onClick={removeEntry('whatsApp', i)}
                />
              </Box>
            ))}
            <Button
              variant="contained"
              onClick={addEntry('whatsApp')}
              sx={{ textTransform: 'none', mt: 1 }}
            >
              {translate('resources.accounts.actions.addNumber')}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          gap: 1,
          justifyContent: 'center',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
        }}
      >
        <Button
          onClick={onCancel}
          variant="contained"
          color="primary"
          size="medium"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            minWidth: { xs: 0, sm: '7.5rem' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {translate('resources.accounts.actions.discard')}
        </Button>
        <Button
          onClick={submit}
          variant="contained"
          color="primary"
          size="medium"
          disabled={!canSubmit}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            minWidth: { xs: 0, sm: '7.5rem' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {translate('resources.accounts.actions.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupMemberDialog;
